import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const uid = () =>
  (window.crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const escapeHtml = (s) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const formatMessage = (text) => {
  let s = escapeHtml(text);
  s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noreferrer">$1</a>');
  s = s.replace(/3\s?3\s?9[\s.]?6\s?4\s?4\s?0\s?7\s?9\s?2/g, '<a href="https://wa.me/393396440792" target="_blank" rel="noreferrer">339 6440792</a>');
  s = s.replace(/pagina\s+Contatti/gi, '<a href="/contatti">pagina Contatti</a>');
  s = s.replace(/\n/g, "<br/>");
  return s;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(uid);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Ciao! 👋 Sono l'assistente di treffe. Chiedimi pure di corsi, date o come iscriverti.",
    },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, open]);

  const send = async (e, override) => {
    e?.preventDefault();
    const text = (override ?? input).trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Ops, problema momentaneo. Scrivici su WhatsApp al 339 6440792." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const quick = [
    "Quali corsi offrite?",
    "A chi sono rivolti i corsi?",
    "Come mi iscrivo?",
    "Quali sono le prossime date?",
    "Rilasciate l'attestato?",
    "Fate corsi in azienda?",
    "Quanto costano i corsi?",
    "Il bonifico rende effettiva l'iscrizione?",
  ];

  return (
    <>
      <button
        data-testid="chat-launcher"
        aria-label="Apri assistente"
        onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-24 md:bottom-6 right-4 md:right-5 z-[67] w-14 h-14 rounded-full flex items-center justify-center shadow-[0_10px_30px_-6px_rgba(214,40,40,0.6)] hover:scale-105 transition-transform overflow-hidden ${open ? "bg-brand-red text-white" : "bg-white border-2 border-brand-red"}`}
      >
        {open ? (
          <X size={24} />
        ) : (
          <img src="/chat-mascot.png" alt="Assistente treffe" className="w-full h-full object-cover" />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="fixed bottom-40 md:bottom-24 right-4 md:right-5 z-[67] w-[calc(100vw-2rem)] sm:w-[380px] h-[520px] max-h-[calc(70dvh-env(safe-area-inset-bottom))] bg-white border border-border shadow-2xl flex flex-col overflow-hidden rounded-2xl"
          >
            <div className="bg-brand-black text-white px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden shrink-0 border-2 border-brand-red">
                <img src="/chat-mascot.png" alt="Mascotte treffe" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="font-heading font-bold leading-none">Assistente treffe</div>
                <div className="text-[11px] text-white/50">Risponde in pochi secondi</div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-brand-paper">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid="chat-message"
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto bg-brand-red text-white"
                      : "mr-auto bg-white border border-border text-brand-black [&_a]:text-brand-red [&_a]:underline [&_a]:font-semibold [&_strong]:font-bold"
                  }`}
                >
                  {m.role === "assistant"
                    ? <span dangerouslySetInnerHTML={{ __html: formatMessage(m.content) }} />
                    : m.content}
                </div>
              ))}
              {loading && (
                <div className="mr-auto bg-white border border-border px-4 py-3 flex gap-1">
                  <span className="w-2 h-2 bg-brand-red rounded-full animate-bounce" />
                  <span className="w-2 h-2 bg-brand-red rounded-full animate-bounce [animation-delay:0.15s]" />
                  <span className="w-2 h-2 bg-brand-red rounded-full animate-bounce [animation-delay:0.3s]" />
                </div>
              )}
              {messages.length <= 1 && !loading && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {quick.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(null, q)}
                      className="text-xs border border-border bg-white px-3 py-1.5 hover:border-brand-red hover:text-brand-red transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={send} className="p-3 border-t border-border flex items-center gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Scrivi un messaggio…"
                className="flex-1 bg-transparent px-2 py-2 text-sm focus:outline-none"
              />
              <button
                type="submit"
                data-testid="chat-send"
                disabled={loading}
                className="w-10 h-10 bg-brand-red text-white flex items-center justify-center hover:bg-brand-black transition-colors disabled:opacity-50"
              >
                <Send size={17} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
